(() => {
  const lockClass = 'vo-header-drawer-lock';
  const headerRevealDuration = 240;

  class VoHeader {
    constructor(root) {
      this.root = root;
      this.headerGroup = root.closest('.shopify-section-group-header-group') || null;
      this.section = root.closest('.section--vo-header.section-header') || root.parentElement;
      this.stickyContainer = this.section;
      this.announcementSection = this.headerGroup?.querySelector('.announcement-bar-section') || null;
      this.mainShell = root.querySelector('.vo-header__main-shell');
      this.drawer = root.querySelector('[data-vo-header-drawer]');
      this.drawerOpenButtons = Array.from(root.querySelectorAll('[data-vo-header-drawer-open]'));
      this.drawerCloseButtons = root.querySelectorAll('[data-vo-header-drawer-close]');
      this.disclosures = Array.from(root.querySelectorAll('[data-vo-header-disclosure]'));
      this.megaOverlay = root.querySelector('[data-vo-header-mega-overlay]');
      this.serviceBar = root.querySelector('[data-vo-header-service]');
      this.brandLinks = Array.from(root.querySelectorAll('.vo-header__brand-link'));
      this.disclosureCloseTimer = null;
      this.headerTopOffset = root.offsetTop;
      this.headerResetOffset = 0;
      this.headerBounds = { top: 0, bottom: 0 };
      this.fullHeaderHeight = root.offsetHeight;
      this.currentHeaderHeight = root.offsetHeight;
      this.currentScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      this.hasScrolledPastHeader = false;
      this.visibilityState = 'default';
      this.announcementIsHidden = false;
      this.hiddenScrollAnchor = this.currentScrollTop;
      this.revealedScrollAnchor = this.currentScrollTop;
      this.headerRevealTimer = null;
      this.revealThreshold = 12;
      this.rehideThreshold = 48;
      this.scrollTicking = false;
      this.pendingScrollTop = this.currentScrollTop;
      this.desktopMenuMedia = window.matchMedia('(min-width: 1024px)');
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      this.initDesktopMenus();
      this.initDrawer();
      this.initBrandLinks();
      this.measureServiceBar();
      this.initStickyBehavior();
    }

    measureServiceBar() {
      const update = () => {
        const serviceHeight = this.serviceBar?.offsetHeight || 0;
        const mainHeight = this.mainShell?.offsetHeight || this.root.offsetHeight;
        const announcementHeight = this.announcementSection?.offsetHeight || 0;
        const effectiveAnnouncementHeight = this.announcementIsHidden ? 0 : announcementHeight;
        const isCompact = this.root.classList.contains('vo-header--compact');
        const serviceCurrentHeight = isCompact ? 0 : serviceHeight;

        this.headerTopOffset = this.section?.offsetTop || this.root.offsetTop;
        this.headerResetOffset = Math.max(this.headerTopOffset - announcementHeight, 0);
        this.fullHeaderHeight = mainHeight + serviceHeight;
        this.currentHeaderHeight = mainHeight + serviceCurrentHeight;
        this.headerBounds = {
          top: this.headerTopOffset,
          bottom: this.headerTopOffset + this.fullHeaderHeight,
        };
        this.root.style.setProperty('--vo-header-service-height', serviceHeight + 'px');
        document.documentElement.style.setProperty('--vo-announcement-height', announcementHeight + 'px');
        document.documentElement.style.setProperty('--announcement-height', effectiveAnnouncementHeight + 'px');
        document.documentElement.style.setProperty('--announcement-height-tablet', effectiveAnnouncementHeight + 'px');
        document.documentElement.style.setProperty('--announcement-height-desktop', effectiveAnnouncementHeight + 'px');
        document.documentElement.style.setProperty('--header-height', this.currentHeaderHeight + 'px');
      };

      this.updateMeasurements = update;
      update();
    }

    initStickyBehavior() {
      const initialScrollTop = this.currentScrollTop;
      this.currentScrollTop = 0;
      this.applyScrollState(initialScrollTop);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
    }

    onResize() {
      this.updateMeasurements?.();
      this.applyScrollState(window.pageYOffset || document.documentElement.scrollTop || 0);
    }

    onScroll() {
      this.pendingScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;

      if (this.scrollTicking) return;

      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        this.applyScrollState(this.pendingScrollTop);
        this.scrollTicking = false;
      });
    }

    applyScrollState(scrollTop) {
      const nextScrollTop = Math.max(scrollTop, 0);
      const delta = nextScrollTop - this.currentScrollTop;

      if (nextScrollTop <= this.headerResetOffset + 2) {
        this.resetStickyState(nextScrollTop);
        this.currentScrollTop = nextScrollTop;
        this.hasScrolledPastHeader = false;
        return;
      }

      if (delta === 0) {
        this.currentScrollTop = nextScrollTop;
        return;
      }

      if (nextScrollTop > this.headerBounds.bottom) {
        this.hasScrolledPastHeader = true;
      }

      this.stickyContainer?.classList.add('shopify-section-header-sticky');

      if (!this.hasScrolledPastHeader) {
        this.currentScrollTop = nextScrollTop;
        return;
      }

      this.setAnnouncementHidden(true, true);

      if (delta > 0) {
        if (this.visibilityState === 'default') {
          this.hideStickyHeader(nextScrollTop);
        } else if (this.visibilityState === 'hidden') {
          this.hiddenScrollAnchor = Math.max(this.hiddenScrollAnchor, nextScrollTop);
        } else if (nextScrollTop - this.revealedScrollAnchor >= this.rehideThreshold) {
          this.hideStickyHeader(nextScrollTop);
        }
      } else if (this.visibilityState === 'hidden') {
        const revealedDistance = this.hiddenScrollAnchor - nextScrollTop;

        if (revealedDistance >= this.revealThreshold) {
          this.revealStickyHeader(nextScrollTop);
        }
      } else if (this.visibilityState === 'revealed') {
        this.revealedScrollAnchor = Math.min(this.revealedScrollAnchor, nextScrollTop);
      }

      this.currentScrollTop = nextScrollTop;
    }

    hideStickyHeader(anchorScrollTop = this.currentScrollTop) {
      if (this.visibilityState === 'hidden') return;
      this.visibilityState = 'hidden';
      this.hiddenScrollAnchor = anchorScrollTop;
      this.clearHeaderRevealTimer();
      document.documentElement.classList.add('vo-header-is-hidden');
      this.setAnnouncementHidden(true, true);
      this.stickyContainer?.classList.remove('animate');
      this.stickyContainer?.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeDisclosures();
    }

    revealStickyHeader(anchorScrollTop = this.currentScrollTop) {
      if (this.visibilityState === 'revealed') return;
      this.visibilityState = 'revealed';
      this.revealedScrollAnchor = anchorScrollTop;
      this.clearHeaderRevealTimer();
      this.setAnnouncementHidden(true, true);
      this.setCompactState(false);
      this.stickyContainer?.classList.add('shopify-section-header-sticky', 'animate');
      this.stickyContainer?.classList.remove('shopify-section-header-hidden');
      this.headerRevealTimer = window.setTimeout(() => {
        document.documentElement.classList.remove('vo-header-is-hidden');
        this.headerRevealTimer = null;
      }, headerRevealDuration);
    }

    resetStickyState(anchorScrollTop = this.currentScrollTop) {
      this.visibilityState = 'default';
      this.hiddenScrollAnchor = anchorScrollTop;
      this.revealedScrollAnchor = anchorScrollTop;
      this.clearHeaderRevealTimer();
      document.documentElement.classList.remove('vo-header-is-hidden');
      this.setAnnouncementHidden(false);
      this.stickyContainer?.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
      this.setCompactState(false);
    }

    clearHeaderRevealTimer() {
      if (!this.headerRevealTimer) return;
      window.clearTimeout(this.headerRevealTimer);
      this.headerRevealTimer = null;
    }

    setAnnouncementHidden(isHidden, shouldAnimate = false) {
      if (!this.announcementSection || this.announcementIsHidden === isHidden) return;

      this.announcementIsHidden = isHidden;
      this.announcementSection.classList.toggle('shopify-section-announcement-sticky', isHidden);
      this.announcementSection.classList.toggle('shopify-section-announcement-hidden', isHidden);
      this.announcementSection.classList.toggle('animate', shouldAnimate && isHidden);
      this.updateMeasurements?.();
    }

    setCompactState(isCompact) {
      if (this.root.classList.contains('vo-header--compact') === isCompact) return;
      this.root.classList.toggle('vo-header--compact', isCompact);
      this.updateMeasurements?.();
    }

    initDesktopMenus() {
      if (!this.disclosures.length) return;

      this.disclosures.forEach((disclosure) => {
        const summary = disclosure.querySelector('summary');

        summary?.setAttribute('aria-expanded', disclosure.open ? 'true' : 'false');

        disclosure.addEventListener('toggle', () => {
          summary?.setAttribute('aria-expanded', disclosure.open ? 'true' : 'false');
          this.updateMegaOverlay();

          if (!disclosure.open) return;

          this.disclosures.forEach((otherDisclosure) => {
            if (otherDisclosure !== disclosure) {
              otherDisclosure.open = false;
            }
          });
        });

        disclosure.addEventListener('mouseenter', () => {
          if (!this.isDesktopMenuHoverEnabled()) return;
          this.clearDisclosureCloseTimer();
          this.openDisclosure(disclosure);
        });

        disclosure.addEventListener('mouseleave', () => {
          if (!this.isDesktopMenuHoverEnabled()) return;
          this.scheduleDisclosureClose();
        });

        disclosure.addEventListener('focusin', () => {
          this.clearDisclosureCloseTimer();
          this.openDisclosure(disclosure);
        });

        disclosure.addEventListener('focusout', (event) => {
          if (disclosure.contains(event.relatedTarget)) return;
          this.scheduleDisclosureClose(0);
        });

        summary?.addEventListener('click', (event) => {
          if (!this.isDesktopMenuHoverEnabled()) return;
          if (event.target.closest('a')) return;
          event.preventDefault();
          this.openDisclosure(disclosure);
        });
      });

      document.addEventListener('click', (event) => {
        const clickedDisclosure = event.target.closest('[data-vo-header-disclosure]');

        if (!clickedDisclosure || !this.root.contains(clickedDisclosure)) {
          this.closeDisclosures();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeDisclosures();
        }
      });

      this.desktopMenuMedia.addEventListener('change', () => {
        this.closeDisclosures();
      });

      if (this.megaOverlay) {
        this.megaOverlay.addEventListener('click', () => {
          this.closeDisclosures();
        });
      }

      if (this.serviceBar) {
        this.serviceBar.addEventListener('mouseenter', () => {
          if (!this.isDesktopMenuHoverEnabled()) return;
          this.clearDisclosureCloseTimer();
        });

        this.serviceBar.addEventListener('mouseleave', () => {
          if (!this.isDesktopMenuHoverEnabled()) return;
          this.scheduleDisclosureClose();
        });
      }
    }

    updateMegaOverlay() {
      if (!this.megaOverlay) return;
      const anyOpen = this.disclosures.some((d) => d.open);
      this.megaOverlay.hidden = !anyOpen;
    }

    isDesktopMenuHoverEnabled() {
      return this.desktopMenuMedia.matches;
    }

    openDisclosure(disclosure) {
      this.disclosures.forEach((otherDisclosure) => {
        otherDisclosure.open = otherDisclosure === disclosure;
      });
    }

    scheduleDisclosureClose(delay = 120) {
      this.clearDisclosureCloseTimer();
      this.disclosureCloseTimer = window.setTimeout(() => {
        this.closeDisclosures();
      }, delay);
    }

    clearDisclosureCloseTimer() {
      if (this.disclosureCloseTimer) {
        window.clearTimeout(this.disclosureCloseTimer);
        this.disclosureCloseTimer = null;
      }
    }

    closeDisclosures() {
      this.clearDisclosureCloseTimer();
      this.disclosures.forEach((disclosure) => {
        disclosure.open = false;
      });
      this.updateMegaOverlay();
    }

    initDrawer() {
      if (!this.drawer || !this.drawerOpenButtons.length) return;

      this.drawerPanel = this.drawer.querySelector('.vo-header__drawer-panel');
      this.drawerSubmenuToggles = Array.from(this.drawer.querySelectorAll('[data-vo-drawer-submenu-toggle]'));
      this.drawerSubmenuBacks = Array.from(this.drawer.querySelectorAll('[data-vo-drawer-submenu-back]'));

      this.drawerOpenButtons.forEach((button) => {
        button.addEventListener('click', () => this.openDrawer());
      });

      this.drawerCloseButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeDrawer());
      });

      this.drawerSubmenuToggles.forEach((button) => {
        button.addEventListener('click', () => {
          const index = button.dataset.voDrawerSubmenuToggle;
          this.openDrawerSubmenu(index);
        });
      });

      this.drawerSubmenuBacks.forEach((button) => {
        button.addEventListener('click', () => {
          this.closeDrawerSubmenus();
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.drawer.hidden) {
          this.closeDrawer();
        }
      });
    }

    openDrawerSubmenu(index) {
      const submenu = this.drawer.querySelector('[data-vo-drawer-submenu="' + index + '"]');
      if (!submenu) return;

      this.closeDrawerSubmenus();

      if (this.drawerPanel) {
        this.drawerPanel.scrollTop = 0;
        this.drawerPanel.classList.add('has-submenu-open');
      }

      submenu.hidden = false;
      requestAnimationFrame(() => {
        submenu.classList.add('is-open');
      });

      const toggle = this.drawer.querySelector('[data-vo-drawer-submenu-toggle="' + index + '"]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    }

    closeDrawerSubmenus() {
      const openSubmenus = this.drawer.querySelectorAll('.vo-header__drawer-submenu.is-open');
      openSubmenus.forEach((submenu) => {
        submenu.classList.remove('is-open');
        submenu.addEventListener('transitionend', function handler() {
          submenu.hidden = true;
          submenu.removeEventListener('transitionend', handler);
        });
      });

      if (this.drawerPanel) {
        this.drawerPanel.classList.remove('has-submenu-open');
      }

      this.drawerSubmenuToggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
      });
    }

    openDrawer() {
      if (!this.drawer) return;

      this.drawer.hidden = false;
      this.drawerOpenButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'true');
      });
      document.body.classList.add(lockClass);
    }

    closeDrawer() {
      if (!this.drawer) return;

      this.closeDrawerSubmenus();
      this.drawer.hidden = true;
      this.drawerOpenButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });
      document.body.classList.remove(lockClass);
    }

    initBrandLinks() {
      if (!this.brandLinks.length) return;

      const normalizePath = (path) => String(path || '').replace(/\/+$/, '') || '/';

      this.brandLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
          if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
          }

          let linkPath;
          try {
            linkPath = new URL(link.href, window.location.origin).pathname;
          } catch (error) {
            return;
          }

          if (normalizePath(linkPath) !== normalizePath(window.location.pathname)) return;

          event.preventDefault();
          this.closeDrawer();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      });
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-vo-header]').forEach((root) => {
      if (root.dataset.voHeaderReady === 'true') return;
      root.dataset.voHeaderReady = 'true';
      new VoHeader(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
